import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, Heart, Compass, Sparkles, GraduationCap, Handshake } from "lucide-react";
import { Section, Card } from "@/components/Section";
import { Joki } from "@/components/Joki";
import { FAQ } from "@/components/FAQ";
import { HOME_FAQ } from "@/lib/faqData";
import { IMAGES, BRAND } from "@/lib/site";

const valeurs = [
  { icon: Heart, titre: "Bienveillance", texte: "Un cadre rassurant où chaque jeune se sent écouté et respecté." },
  { icon: Users, titre: "Petits effectifs", texte: "Des classes à taille humaine pour favoriser l'attention individuelle." },
  { icon: Compass, titre: "Suivi personnalisé", texte: "Un accompagnement éducatif et d'orientation tout au long du parcours." },
  { icon: Sparkles, titre: "Inclusion", texte: "Une école ouverte à tous, qui valorise les différences et les talents." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" },
  }),
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-slate-dark">
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Atelier du lycée Joseph Wresinski"
            className="h-full w-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-slate-dark via-brand-slate-dark/80 to-brand-slate-dark/20" />
        </div>

        <div className="relative mx-auto max-w-7xl px-5 py-24 sm:py-32 lg:px-10 lg:py-40">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="max-w-3xl text-brand-cream"
          >
            <p className="mb-5 inline-block rounded-full bg-brand-sand/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-sand">
              Lycée pro privé · {BRAND.ville}
            </p>
            <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              {BRAND.baseline}.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-brand-cream/85">
              Au lycée Joseph Wresinski, nous croyons que chaque jeune mérite un
              accompagnement humain pour révéler ses talents. Petits effectifs,
              encadrement bienveillant et formations qualifiantes en lien avec
              les entreprises d'Angers et sa région.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/formations"
                data-testid="hero-cta-formations"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-sand-dark px-6 py-3.5 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-sand"
              >
                Découvrir nos formations
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact?type=inscription"
                data-testid="hero-cta-inscription"
                className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 bg-brand-cream/10 px-6 py-3.5 text-sm font-semibold text-brand-cream backdrop-blur transition-all duration-300 hover:bg-brand-cream hover:text-brand-slate-dark"
              >
                Inscrire mon enfant
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-brand-cream/90 underline-offset-4 transition-colors hover:text-brand-sand hover:underline"
              >
                Nous contacter
              </Link>
            </div>
          </motion.div>

          {/* Joki flottant dans le hero */}
          <div className="pointer-events-none absolute right-6 top-1/2 hidden -translate-y-1/2 lg:block" aria-hidden="true">
            <Joki pose="study" size={280} float className="drop-shadow-2xl" />
          </div>
        </div>
      </section>

      {/* CHIFFRES */}
      <Section wrapper="bg-brand-sand-wrapper/40">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { chiffre: "94%", label: "de réussite au Bac Pro" },
            { chiffre: "18", label: "élèves en moyenne par classe" },
            { chiffre: "40+", label: "entreprises partenaires" },
            { chiffre: "1965", label: "année de fondation" },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              data-testid={`stat-${i}`}
              className="rounded-3xl border border-black/5 bg-brand-cream p-8"
            >
              <p className="font-heading text-4xl font-bold text-brand-slate-dark lg:text-5xl">
                {s.chiffre}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* VALEURS */}
      <Section
        eyebrow="Nos valeurs"
        title="Un accompagnement humain, au cœur de notre projet"
        subtitle="Notre mission : offrir un cadre où chaque jeune se construit avec confiance, prêt à entrer dans la vie active ou à poursuivre ses études."
      >
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {valeurs.map((v, i) => (
            <motion.div
              key={v.titre}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <Card data-testid={`valeur-${i}`}>
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-sage/15 text-brand-sage-dark">
                  <v.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-heading text-xl font-semibold text-brand-slate-dark">
                  {v.titre}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {v.texte}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FORMATIONS PREVIEW */}
      <Section
        eyebrow="Nos formations"
        title="Des filières professionnelles porteuses"
        subtitle="Du CAP au Bac Pro, nos formations préparent à des métiers concrets avec une forte demande sur le bassin d'emploi angevin."
      >
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr]">
          <Link
            to="/formations"
            data-testid="formation-card-featured"
            className="group relative overflow-hidden rounded-3xl bg-brand-slate"
          >
            <img
              src={IMAGES.workshop}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-slate-dark/85 to-transparent" />
            <div className="relative flex h-full min-h-[280px] flex-col justify-end p-8 text-brand-cream">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-sand">
                4 filières professionnelles
              </p>
              <p className="mt-3 font-heading text-2xl font-bold lg:text-3xl">
                Commerce, Animation, Petite Enfance
              </p>
              <p className="mt-3 text-sm text-brand-cream/80">
                Découvrir nos parcours du CAP au Bac Pro
              </p>
              <ArrowRight className="mt-5 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>

          <Link
            to="/accompagnement"
            data-testid="formation-card-accompagnement"
            className="group flex flex-col justify-between rounded-3xl border border-black/5 bg-brand-sage/15 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
          >
            <Compass className="h-7 w-7 text-brand-sage-dark" />
            <div>
              <h3 className="mt-6 font-heading text-xl font-bold text-brand-slate-dark">
                Suivi individualisé
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Tutorat, soutien scolaire, aide à l'orientation, projets personnalisés.
              </p>
            </div>
          </Link>

          <Link
            to="/partenaires"
            data-testid="formation-card-partenaires"
            className="group flex flex-col justify-between rounded-3xl border border-black/5 bg-brand-sand-light/40 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
          >
            <Handshake className="h-7 w-7 text-brand-slate-dark" />
            <div>
              <h3 className="mt-6 font-heading text-xl font-bold text-brand-slate-dark">
                Stages & entreprises
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Un réseau solide de partenaires pour l'immersion professionnelle.
              </p>
            </div>
          </Link>
        </div>
      </Section>

      {/* CITATION */}
      <Section wrapper="bg-brand-slate-dark text-brand-cream">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-sand">
              Notre engagement
            </p>
            <p className="mt-6 font-heading text-2xl leading-snug sm:text-3xl lg:text-4xl">
              « Aucun jeune ne doit être laissé sur le bord du chemin. Notre rôle
              est de lui donner les clés pour réussir, à son rythme, avec
              confiance. »
            </p>
            <p className="mt-6 text-sm text-brand-cream/70">
              — L'équipe éducative
            </p>
          </div>
          <div className="relative aspect-[5/4] overflow-hidden rounded-3xl">
            <img src={IMAGES.values} alt="Élève accompagné par un enseignant" className="h-full w-full object-cover" />
          </div>
        </div>
      </Section>

      {/* CTA FINAL */}
      <Section>
        <div className="relative overflow-hidden rounded-3xl bg-brand-sand-wrapper p-10 lg:p-16">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-sand/40 blur-3xl" />
          <div className="relative grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold tracking-tight text-brand-slate-dark sm:text-4xl">
                Prêts à construire l'avenir de votre enfant ?
              </h2>
              <p className="mt-4 max-w-xl text-base text-muted-foreground">
                Prenez rendez-vous, demandez une plaquette ou venez nous rencontrer
                lors de nos journées portes ouvertes. Nos équipes sont à votre
                écoute.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="cta-footer-contact"
                className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
              >
                <GraduationCap className="h-4 w-4" />
                Nous contacter
              </Link>
              <Link
                to="/telechargements"
                data-testid="cta-footer-download"
                className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 bg-transparent px-6 py-3.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
              >
                Télécharger la plaquette
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* FAQ — SEO/GEO/GSO */}
      <FAQ
        items={HOME_FAQ}
        eyebrow="FAQ"
        title="Vos questions sur le lycée Joseph Wresinski d'Angers"
        subtitle="Tout ce que les parents, futurs élèves et partenaires nous demandent le plus souvent. Une réponse manque ? Contactez-nous, nous serons ravis d'y répondre."
        schemaId="faq-home"
      />
    </div>
  );
}
